import {
    Box,
    Container,
    Flex,
    Heading,
    HStack,
    Icon,
    Link,
    SimpleGrid,
    Stack,
    Text,
} from '@chakra-ui/react';
import { Fragment } from 'react';
import type { IconType } from 'react-icons';
import {
    LuArrowDown,
    LuArrowRight,
    LuBuilding2,
    LuChartColumnIncreasing,
    LuFileChartColumn,
    LuFileCheck2,
    LuFolderKanban,
    LuHandshake,
    LuLandmark,
    LuLockKeyhole,
} from 'react-icons/lu';
import { useI18n } from 'src/i18n/I18nContext';

interface OutcomeDefinition {
    key: 'gaps' | 'funds' | 'partners';
    icon: IconType;
    accentColor: string;
    iconBackground: string;
    detailTarget: string;
}

const outcomeDefinitions: OutcomeDefinition[] = [
    {
        key: 'gaps',
        icon: LuChartColumnIncreasing,
        accentColor: 'blue.solid',
        iconBackground: 'blue.subtle',
        detailTarget: '#how-it-works',
    },
    {
        key: 'funds',
        icon: LuLockKeyhole,
        accentColor: 'teal.solid',
        iconBackground: 'teal.subtle',
        detailTarget: '#funding-chain-project-fund',
    },
    {
        key: 'partners',
        icon: LuFileCheck2,
        accentColor: 'orange.solid',
        iconBackground: 'orange.subtle',
        detailTarget: '#funding-chain-partner-spending',
    },
];

interface ChainDefinition {
    key: 'funder' | 'commitment' | 'projectFund' | 'partnerTransfer' | 'expenseDocument' | 'report';
    icon: IconType;
    id?: string;
}

const chainDefinitions: ChainDefinition[] = [
    { key: 'funder', icon: LuBuilding2 },
    { key: 'commitment', icon: LuHandshake },
    { key: 'projectFund', icon: LuFolderKanban, id: 'funding-chain-project-fund' },
    { key: 'partnerTransfer', icon: LuLandmark },
    { key: 'expenseDocument', icon: LuFileCheck2, id: 'funding-chain-partner-spending' },
    { key: 'report', icon: LuFileChartColumn },
];

const CoreOutcomes = () => {
    const { t } = useI18n();

    return (
        <Box id='product' as='section' py={{ base: 12, md: 20 }} scrollMarginTop='5rem'>
            <Container maxW={{ base: '2xl', sm: '3xl', md: '4xl', lg: '6xl' }}>
                <Stack align='center' gap='3' mb={{ base: 8, md: 12 }} textAlign='center'>
                    <Text color='blue.fg' fontSize='sm' fontWeight='bold' letterSpacing='wide' textTransform='uppercase'>
                        {t('outcomes.eyebrow')}
                    </Text>
                    <Heading maxW='4xl' fontSize={{ base: '3xl', md: '4xl' }} lineHeight='1.15'>
                        {t('outcomes.title')}
                    </Heading>
                    <Text maxW='3xl' color='fg.muted' fontSize={{ base: 'md', md: 'lg' }} lineHeight='1.7'>
                        {t('outcomes.intro')}
                    </Text>
                </Stack>

                <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 5, md: 6 }} alignItems='stretch'>
                    {outcomeDefinitions.map((outcome, index) => (
                        <Stack
                            as='article'
                            data-testid='outcome-card'
                            key={outcome.key}
                            h='full'
                            gap='5'
                            p={{ base: 5, md: 6 }}
                            rounded='2xl'
                            borderWidth='1px'
                            borderColor='border.subtle'
                            borderTopWidth='4px'
                            borderTopColor={outcome.accentColor}
                            bg='bg.panel'
                            boxShadow='lg'
                        >
                            <Flex align='center' justify='space-between' gap='3'>
                                <Flex
                                    boxSize='11'
                                    align='center'
                                    justify='center'
                                    rounded='xl'
                                    bg={outcome.iconBackground}
                                    color={outcome.accentColor}
                                >
                                    <Icon as={outcome.icon} boxSize='6' />
                                </Flex>
                                <Text color='fg.subtle' fontSize='sm' fontWeight='bold'>
                                    0{index + 1}
                                </Text>
                            </Flex>
                            <Stack gap='3' flex='1'>
                                <Heading as='h3' fontSize='xl' lineHeight='1.25'>
                                    {t(`outcomes.items.${outcome.key}.title`)}
                                </Heading>
                                <Text color='fg.muted' lineHeight='1.7'>
                                    {t(`outcomes.items.${outcome.key}.text`)}
                                </Text>
                            </Stack>
                            <Link
                                href={outcome.detailTarget}
                                alignSelf='flex-start'
                                display='inline-flex'
                                alignItems='center'
                                gap='2'
                                color='blue.fg'
                                fontWeight='semibold'
                                rounded='sm'
                                _hover={{ textDecoration: 'underline' }}
                                _focusVisible={{ outline: '2px solid', outlineColor: 'blue.focusRing', outlineOffset: '3px' }}
                            >
                                {t(`outcomes.items.${outcome.key}.link`)}
                                <Icon as={LuArrowRight} aria-hidden='true' />
                            </Link>
                        </Stack>
                    ))}
                </SimpleGrid>

                <Stack
                    id='funding-chain'
                    mt={{ base: 12, md: 20 }}
                    gap={{ base: 7, md: 9 }}
                    p={{ base: 5, sm: 7, md: 10 }}
                    rounded='2xl'
                    bg='bg.subtle'
                    borderWidth='1px'
                    borderColor='border.subtle'
                    scrollMarginTop='5rem'
                >
                    <Stack align='center' gap='3' textAlign='center'>
                        <Text color='teal.fg' fontSize='sm' fontWeight='bold' letterSpacing='wide' textTransform='uppercase'>
                            {t('outcomes.chain.eyebrow')}
                        </Text>
                        <Heading as='h2' maxW='3xl' fontSize={{ base: '2xl', md: '3xl' }}>
                            {t('outcomes.chain.title')}
                        </Heading>
                        <Text maxW='3xl' color='fg.muted' lineHeight='1.7'>
                            {t('outcomes.chain.intro')}
                        </Text>
                    </Stack>

                    <Flex role='list' direction={{ base: 'column', lg: 'row' }} align='stretch' w='full'>
                        {chainDefinitions.map((node, index) => (
                            <Fragment key={node.key}>
                                <Stack
                                    id={node.id}
                                    role='listitem'
                                    data-testid='funding-chain-node'
                                    flex='1'
                                    minW='0'
                                    align='center'
                                    justify='center'
                                    gap='3'
                                    p={{ base: 4, lg: 3 }}
                                    rounded='xl'
                                    borderWidth='1px'
                                    borderColor='border.subtle'
                                    bg='bg.panel'
                                    boxShadow='sm'
                                    textAlign='center'
                                    scrollMarginTop='6rem'
                                >
                                    <Flex boxSize='10' align='center' justify='center' rounded='full' bg='teal.subtle' color='teal.fg'>
                                        <Icon as={node.icon} boxSize='5' />
                                    </Flex>
                                    <Stack gap='1'>
                                        <Text fontWeight='bold' lineHeight='1.25'>
                                            {t(`outcomes.chain.nodes.${node.key}.title`)}
                                        </Text>
                                        <Text color='fg.muted' fontSize='xs' lineHeight='1.4'>
                                            {t(`outcomes.chain.nodes.${node.key}.text`)}
                                        </Text>
                                    </Stack>
                                </Stack>
                                {index < chainDefinitions.length - 1 && (
                                    <Flex
                                        role='presentation'
                                        minH={{ base: '2.5rem', lg: 'auto' }}
                                        minW={{ base: 'auto', lg: '2rem' }}
                                        align='center'
                                        justify='center'
                                        color='teal.fg'
                                    >
                                        <Icon as={LuArrowDown} display={{ base: 'block', lg: 'none' }} aria-hidden='true' />
                                        <Icon as={LuArrowRight} display={{ base: 'none', lg: 'block' }} aria-hidden='true' />
                                    </Flex>
                                )}
                            </Fragment>
                        ))}
                    </Flex>
                </Stack>
            </Container>
        </Box>
    );
};

export default CoreOutcomes;
